import React, { useCallback, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { Link } from 'react-router-dom';

import { createLocale } from 'locales';

import { TERMS_AND_CONDITIONS_DOC } from 'constants/signup-flow-constants';

import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';
import Typography from 'components-new/typography/typography';

import SubmitButton from 'form-elements/submit-button';
import TextField from 'form-elements/text-field';
import PasswordField from 'form-elements/password-field';
import SelectField from 'form-elements/select-field';
import CheckboxField from 'form-elements/checkbox-field';

import useRecaptcha from 'hooks/use-recaptcha';
import useRedirect from 'hooks/use-redirect';

import useFreelancerSignUp from 'state/remote/remote-freelancer/use-freelancer-sign-up';
import useFreelancerLogin from 'state/remote/remote-freelancer/use-freelancer-login';

import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';
import freelancerRoutes from '../freelancer-routes';

import signUpFormValidate from './schema';

const signUpLocalize = createLocale('freelancerSignUpLocales');
const globalLocalize = createLocale('global');
const personalInfoLocalize = createLocale(
  'freelancerPersonalInformationPageLocales',
);

const CheckboxLabel = (
  <Typography as="h2" variant="body-medium" className="text-gray500">
    {`${signUpLocalize('agreeWith')} `}
    <a
      href={TERMS_AND_CONDITIONS_DOC}
      target="_blank"
      rel="noreferrer"
      className="text-current text-purple text-body-medium-bold"
    >
      <b>{globalLocalize('termsAndConditions')}</b>
    </a>
  </Typography>
);

const SignUp = (): JSX.Element => {
  const redirect = useRedirect();
  const { signUp, initialValues, options, isLoading } = useFreelancerSignUp();
  const {
    login,
    isLoading: loginIsLoading,
    error: loginError,
  } = useFreelancerLogin();
  const {
    validateRecaptcha,
    reCaptchaLoaded,
    error: recaptchaError,
  } = useRecaptcha();

  const onSignUpClicked = useCallback(
    async (values: Parameters<typeof signUp>[0]) => {
      const validateResponse = await validateRecaptcha('freelancerSignup');
      if (validateResponse.success) {
        const data = await signUp(values);
        if (data.success) {
          await login({
            password: values.password1,
            username: values.username,
          });
          showSuccessToast({
            description: signUpLocalize('signupSuccess'),
            title: globalLocalize('notification'),
          });
          redirect(freelancerRoutes.welcome.path);
        }
        if (data.errors) {
          // using data.errors here, since the error object from useFreelancerSignup is too standard (400)
          const { errors } = data;
          Object.keys(errors).forEach((key) => {
            showErrorToast({
              description: errors[key],
              title: globalLocalize('error'),
            });
          });
        }
      } else {
        showErrorToast({
          description: signUpLocalize('signupError'),
          title: globalLocalize('notification'),
        });
      }
    },
    [validateRecaptcha, signUp, login, redirect],
  );

  useEffect(() => {
    if (recaptchaError)
      showErrorToast({
        description: recaptchaError,
        title: globalLocalize('error'),
      });
    if (loginError)
      showErrorToast({
        description: loginError.message,
        title: globalLocalize('error'),
      });
  });

  return (
    <FreelancerSignUpPageContainer
      showHeader
      headerTitle={signUpLocalize('title')}
      showGoBackButton
      className="flex flex-col items-center"
    >
      <Formik
        initialValues={{ ...initialValues, termsAndConditions: false }}
        validationSchema={signUpFormValidate}
        onSubmit={onSignUpClicked}
      >
        <Form className="mb-16 w-full">
          <TextField
            name="first_name"
            type="text"
            className="mt-4"
            title={personalInfoLocalize('formFirstName')}
            placeholder=""
          />
          <TextField
            name="last_name"
            type="text"
            className="mt-4"
            title={personalInfoLocalize('formLastName')}
            placeholder=""
          />
          <TextField
            name="email"
            type="email"
            className="mt-4"
            title={globalLocalize('email')}
            placeholder="email@mail.com"
          />
          <TextField
            name="username"
            type="text"
            className="mt-4"
            title={globalLocalize('userName')}
          />
          <PasswordField
            name="password1"
            className="mt-4"
            title={globalLocalize('password')}
            placeholder="******"
          />
          <PasswordField
            name="password2"
            className="mt-2"
            placeholder={globalLocalize('passwordConfirmation')}
          />
          <SelectField
            name="acquainted"
            className="mt-4"
            title={signUpLocalize('howDoYouKnowTadaah')}
            options={options}
          />
          <div className="flex flex-row items-center justify-start w-full h-10  bg-background mt-3">
            <CheckboxField
              name="termsAndConditions"
              checkboxClassName="mr-3"
              labelComponent={CheckboxLabel}
              className="mt-3 bg-background w-full h-10"
              required
            />
          </div>
          <SubmitButton
            type="submit"
            label={globalLocalize('signUp')}
            variant="primary"
            className="w-full justify-center mt-10"
            loading={
              isLoading ||
              loginIsLoading ||
              (!reCaptchaLoaded && !recaptchaError)
            }
            disabled={!reCaptchaLoaded}
          />
          <Typography
            as="h3"
            variant="body-large"
            className="text-gray500 justify-center mt-3 w-full"
          >
            {signUpLocalize('alreadyHaveAccount')}{' '}
            <Link
              to={freelancerRoutes.login.path}
              className="text-current text-purple text-body-large-bold"
            >
              {' '}
              <b>{globalLocalize('clickHere')}</b>
            </Link>{' '}
            {signUpLocalize('toLogin')}
          </Typography>
        </Form>
      </Formik>
    </FreelancerSignUpPageContainer>
  );
};

export default SignUp;
